import React from "react";
import { IndianRupee } from "lucide-react";
import { HotelListItem, UpdateHotelRequest } from "@repo/types";
import { SectionCard, DetailItem } from "./HotelCommon";

interface TaxSectionProps {
  editForm: Partial<UpdateHotelRequest>;
  isEditMode: boolean;
  setEditForm: (form: Partial<UpdateHotelRequest>) => void;
  selectedHotel: HotelListItem;
}

export const TaxSection: React.FC<TaxSectionProps> = ({
  editForm,
  isEditMode,
  setEditForm,
  selectedHotel,
}) => {
  const currency = selectedHotel.CurrencySymbol || "$";
  return (
    <SectionCard
      title="Tax Configuration"
      icon={() => <span className="text-xs font-bold">{currency}</span>}
    >
      <DetailItem
        label="GST"
        value={`${editForm.GST || 0}%`}
        icon={IndianRupee}
        isEdit={isEditMode}
        onChange={(val) =>
          setEditForm({ ...editForm, GST: Number(val.replace("%", "")) })
        }
      />
      <DetailItem
        label="Food GST"
        value={`${editForm.FoodGST || 0}%`}
        icon={IndianRupee}
        isEdit={isEditMode}
        onChange={(val) =>
          setEditForm({ ...editForm, FoodGST: Number(val.replace("%", "")) })
        }
      />
      {isEditMode ? (
        <div className="flex items-start gap-3 p-3 rounded-xl bg-white dark:bg-[#1b254b] border border-blue-50 dark:border-white/10 shadow-sm">
          <div className="p-2 rounded-lg border bg-blue-50 dark:bg-blue-900/20 border-blue-100 dark:border-blue-900/30 text-blue-500 dark:text-blue-400 shrink-0">
            <span className="text-xs font-bold">
              {editForm.CurrencySymbol || selectedHotel.CurrencySymbol || "$"}
            </span>
          </div>

          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-black text-gray-400 dark:text-slate-500 uppercase tracking-wider">
              Currency
            </p>

            <select
              value={
                editForm.CurrencySymbol || selectedHotel.CurrencySymbol || ""
              }
              onChange={(e) =>
                setEditForm({
                  ...editForm,
                  CurrencySymbol: e.target.value,
                })
              }
              className="w-full bg-transparent text-sm font-bold text-gray-900 dark:text-slate-100 focus:outline-none focus:ring-0 cursor-pointer"
            >
              <option
                value=""
                className="dark:bg-[#111c44] dark:text-slate-400"
              >
                Select Currency
              </option>
              <option
                value="₹"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                ₹ - Indian Rupee (INR)
              </option>
              <option
                value="$"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                $ - US Dollar (USD)
              </option>
              <option
                value="€"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                € - Euro (EUR)
              </option>
              <option
                value="£"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                £ - British Pound (GBP)
              </option>
              <option
                value="¥"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                ¥ - Japanese Yen (JPY)
              </option>
              <option
                value="₩"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                ₩ - South Korean Won (KRW)
              </option>
              <option
                value="₽"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                ₽ - Russian Ruble (RUB)
              </option>
              <option
                value="₺"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                ₺ - Turkish Lira (TRY)
              </option>
              <option
                value="₫"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                ₫ - Vietnamese Dong (VND)
              </option>
              <option
                value="₪"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                ₪ - Israeli Shekel (ILS)
              </option>
              <option
                value="₱"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                ₱ - Philippine Peso (PHP)
              </option>
              <option
                value="₦"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                ₦ - Nigerian Naira (NGN)
              </option>
              <option
                value="฿"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                ฿ - Thai Baht (THB)
              </option>
              <option
                value="₴"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                ₴ - Ukrainian Hryvnia (UAH)
              </option>
              <option
                value="₡"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                ₡ - Costa Rican Colon (CRC)
              </option>
              <option
                value="₲"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                ₲ - Paraguayan Guarani (PYG)
              </option>
              <option
                value="₵"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                ₵ - Ghanaian Cedi (GHS)
              </option>
              <option
                value="₸"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                ₸ - Kazakhstani Tenge (KZT)
              </option>
              <option
                value="₮"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                ₮ - Mongolian Tugrik (MNT)
              </option>
              <option
                value="₭"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                ₭ - Laotian Kip (LAK)
              </option>
              <option
                value="د.إ"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                د.إ - UAE Dirham (AED)
              </option>
              <option
                value="﷼"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                ﷼ - Saudi Riyal (SAR)
              </option>
              <option
                value="د.ك"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                د.ك - Kuwaiti Dinar (KWD)
              </option>
              <option
                value="د.ب"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                د.ب - Bahraini Dinar (BHD)
              </option>
              <option
                value="ر.ع."
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                ر.ع. - Omani Rial (OMR)
              </option>
              <option
                value="ر.ق"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                ر.ق - Qatari Riyal (QAR)
              </option>
              <option
                value="₣"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                ₣ - Swiss Franc (CHF)
              </option>
              <option
                value="kr"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                kr - Scandinavian Krona
              </option>
              <option
                value="R$"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                R$ - Brazilian Real (BRL)
              </option>
              <option
                value="A$"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                A$ - Australian Dollar (AUD)
              </option>
              <option
                value="C$"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                C$ - Canadian Dollar (CAD)
              </option>
              <option
                value="NZ$"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                NZ$ - New Zealand Dollar (NZD)
              </option>
              <option
                value="S$"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                S$ - Singapore Dollar (SGD)
              </option>
              <option
                value="HK$"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                HK$ - Hong Kong Dollar (HKD)
              </option>
              <option
                value="RM"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                RM - Malaysian Ringgit (MYR)
              </option>
              <option
                value="Rp"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                Rp - Indonesian Rupiah (IDR)
              </option>
              <option
                value="₿"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                ₿ - Bitcoin (BTC)
              </option>
            </select>
          </div>
        </div>
      ) : (
        <DetailItem
          label="Currency"
          value={selectedHotel.CurrencySymbol || "N/A"}
          icon={() => (
            <span className="text-xs font-bold">
              {selectedHotel.CurrencySymbol}
            </span>
          )}
        />
      )}
    </SectionCard>
  );
};
